import type { MedplumClient } from '@medplum/core';
import type { CarePlan, Goal, ServiceRequest } from '@medplum/fhirtypes';

import { type PhasedInstanceSpec, decodeInstanceSpec } from '@eluve/plan-shapes';

// A FHIR reference's logical id, robust to relative (`Goal/abc`), absolute
// (`https://server/fhir/Goal/abc`), and versioned (`Goal/abc/_history/2`)
// forms: drop any `_history` version suffix, then take the segment after the
// resource type.
function referenceId(reference: string | undefined): string | undefined {
  if (!reference) {
    return undefined;
  }
  const [withoutVersion] = reference.split('/_history/');
  return withoutVersion?.split('/').at(-1);
}

// Drains every page via Medplum's paginated generator so a large plan's
// children aren't silently truncated to the first page — the accept bundle
// reconciles old children against this list, so a partial read would leave
// orphans.
async function fetchCarePlanChildren(
  client: MedplumClient,
  carePlan: CarePlan,
): Promise<{ serviceRequests: ServiceRequest[]; goals: Goal[] }> {
  const serviceRequests: ServiceRequest[] = [];
  if (carePlan.id) {
    for await (const page of client.searchResourcePages('ServiceRequest', {
      'based-on': `CarePlan/${carePlan.id}`,
      _count: 200,
    })) {
      serviceRequests.push(...page);
    }
  }

  const goalIds = (carePlan.goal ?? [])
    .map((g) => referenceId(g.reference))
    .filter((id): id is string => Boolean(id));
  const goals: Goal[] = [];
  if (goalIds.length > 0) {
    for await (const page of client.searchResourcePages('Goal', {
      _id: goalIds.join(','),
      _count: 200,
    })) {
      goals.push(...page);
    }
  }

  return { serviceRequests, goals };
}

// Loads a CarePlan's children and decodes the stored instance spec — the
// turn-zero the Care Plan tab renders before any tailoring emission and the
// baseline its review pane diffs against, and the structured view the patient
// profile renders read-only. Returns a null `seed` when the CarePlan predates
// tailoring (no `instantiatesCanonical`) or otherwise can't be decoded into a
// phased instance; callers fall back to a plainer view.
export async function loadCarePlanInstance(
  client: MedplumClient,
  carePlan: CarePlan,
): Promise<{
  // `decodeInstanceSpec` only recovers phased instances; a verbatim
  // (continuous/episodic) CarePlan has no decodable spine, so its seed is null
  // and callers fall back to a plainer view.
  seed: PhasedInstanceSpec | null;
  serviceRequests: ServiceRequest[];
  goals: Goal[];
}> {
  const { serviceRequests, goals } = await fetchCarePlanChildren(
    client,
    carePlan,
  );
  return {
    seed: decodeInstanceSpec(carePlan, serviceRequests, goals),
    serviceRequests,
    goals,
  };
}
